import { memo, useMemo } from 'react';
import { Box } from '@mui/material';
import { NumberInput, SelectInput, TextField, TextInput, required } from 'react-admin';
import { Tooltip } from '../../components/Tooltip';

/**
 * Campos de estoque por ponto de troca (aba "Estoque" do produto).
 *
 * IMPORTANTE: aqui NÃO se usa <ArrayInput source="places">.
 * A aba "Resgate" já tem um <ArrayInput source="places"> e o <TabbedForm> mantém
 * TODAS as abas montadas (aba inativa só ganha `display: none`). Dois
 * <ArrayInput> no mesmo source = dois useFieldArray({ name: 'places' }), e o
 * react-hook-form só sincroniza instâncias no `setValue`/`reset` — nunca no
 * `remove`. A segunda instância ficava com a lista velha, seus inputs
 * re-registravam `places.<índice removido>.*` e ressuscitavam uma linha
 * fantasma no form; na remoção seguinte o fantasma virava linha visível e era
 * preenchido com os valores do record naquele índice — a "linha duplicada".
 *
 * Por isso as linhas abaixo são renderizadas na mão, com source absoluto
 * (`places.0.defaultStock`, ...). Só a aba Resgate governa o tamanho do array;
 * esta tela apenas edita os campos de estoque das linhas existentes.
 */

const stockChoices = [
  { id: 'showcase', name: 'Mostruário' },
  { id: 'warehouse', name: 'Depósito' },
];

const sxStyles = {
  list: {
    '& p': {
      color: 'grey',
      marginRight: '1em',
      fontWeight: 'bold',
    },
  },
  placeName: {
    padding: '1em 0 0.5em 0',
    width: '100%',
  },
  fields: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    alignItems: 'flex-start',
  },
};

const StockPlaceRowInner = ({ index, place, placeOpts }) => {
  const placeName = useMemo(() => {
    if (place?.place?.name) { return place.place.name; }
    return placeOpts.find((opt) => opt.id === place?.placeId)?.name || '';
  }, [place?.placeId, place?.place, placeOpts]);

  if (!place?.placeId) { return null; }

  const source = (field) => `places.${index}.${field}`;

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        source="name"
        record={{ name: placeName }}
        component="p"
        variant="subtitle1"
        sx={sxStyles.placeName}
      />
      <Box sx={sxStyles.fields}>
        <SelectInput
          source={source('defaultStock')}
          choices={stockChoices}
          label="Estoque padrão"
          validate={[required()]}
          sx={{ width: 150 }}
        />
        <TextInput
          source={source('showcase')}
          label="Mostruário"
          disabled
          sx={{ width: 120 }}
        />
        <TextInput
          source={source('warehouse')}
          label="Depósito"
          disabled
          sx={{ width: 120 }}
        />
        <Tooltip title="Este campo é utilizado apenas para o relatório de sugestão de compra." placement="top-start">
          <NumberInput
            source={source('minimumStock')}
            label="Estoque mínimo"
            min={0}
            sx={{ width: 160 }}
          />
        </Tooltip>
      </Box>
    </Box>
  );
};

const StockPlaceRow = memo(StockPlaceRowInner);

export const StockPlacesFields = ({ places, placeOpts = [] }) => {
  if (!Array.isArray(places) || !places.length) { return null; }

  return (
    <Box sx={sxStyles.list}>
      {places.map((place, index) => (
        <StockPlaceRow
          key={place?.placeId || `place-${index}`}
          index={index}
          place={place}
          placeOpts={placeOpts}
        />
      ))}
    </Box>
  );
};

export default StockPlacesFields;
